import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';
import Icon from '../../../components/AppIcon';

const LeadDiscoveryPanel = ({ 
  onStartDiscovery, 
  isProcessing, 
  discoveredLeads = [],
  onSelectLeads,
  selectedLeads = []
}) => {
  const [discoveryConfig, setDiscoveryConfig] = useState({
    sources: {
      linkedin: true,
      web_scraping: true,
      social_media: false,
      directories: true,
      databases: false
    },
    criteria: {
      industry: '',
      position: '',
      location: '',
      companySize: '',
      keywords: ''
    },
    advanced: {
      maxResults: 1000,
      aiFiltering: true,
      duplicateDetection: true,
      realTimeValidation: false
    }
  });

  const handleSourceToggle = (source) => {
    setDiscoveryConfig(prev => ({
      ...prev,
      sources: {
        ...prev?.sources,
        [source]: !prev?.sources?.[source]
      }
    }));
  };

  const handleCriteriaChange = (field, value) => {
    setDiscoveryConfig(prev => ({
      ...prev,
      criteria: {
        ...prev?.criteria,
        [field]: value
      }
    }));
  };

  const handleAdvancedToggle = (option) => {
    setDiscoveryConfig(prev => ({
      ...prev,
      advanced: {
        ...prev?.advanced,
        [option]: !prev?.advanced?.[option]
      }
    }));
  };

  const handleLeadSelect = (leadId) => {
    const isSelected = selectedLeads?.includes(leadId);
    if (isSelected) {
      onSelectLeads?.(selectedLeads?.filter(id => id !== leadId));
    } else {
      onSelectLeads?.([...selectedLeads, leadId]);
    }
  };

  const handleSelectAll = () => {
    const allLeadIds = discoveredLeads?.map(lead => lead?.id) || [];
    if (selectedLeads?.length === allLeadIds?.length) {
      onSelectLeads?.([]);
    } else {
      onSelectLeads?.(allLeadIds);
    }
  };

  const getValidationStatusColor = (status) => {
    switch (status) {
      case 'validated': return 'text-green-600 bg-green-50';
      case 'pending': return 'text-yellow-600 bg-yellow-50';
      case 'needs_review': return 'text-red-600 bg-red-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  const getQualityScoreColor = (score) => {
    if (score >= 80) return 'text-green-600';
    if (score >= 60) return 'text-yellow-600';
    return 'text-red-600';
  };

  return (
    <div className="space-y-6">
      {/* Discovery Configuration */}
      <div className="bg-card border border-border rounded-lg p-6">
        <h2 className="text-xl font-semibold text-foreground mb-4">
          AI-Powered Lead Discovery
        </h2>
        
        {/* Data Sources */}
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Data Sources
            </label>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {Object?.entries(discoveryConfig?.sources)?.map(([source, enabled]) => (
                <div key={source} className="flex items-center space-x-2">
                  <Checkbox
                    id={source}
                    checked={enabled}
                    onChange={() => handleSourceToggle(source)}
                  />
                  <label 
                    htmlFor={source} 
                    className="text-sm text-foreground capitalize cursor-pointer"
                  >
                    {source?.replace('_', ' ')}
                  </label>
                </div>
              ))}
            </div>
          </div>

          {/* Search Criteria */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Industry"
              placeholder="e.g., Technology, Healthcare"
              value={discoveryConfig?.criteria?.industry}
              onChange={(e) => handleCriteriaChange('industry', e?.target?.value)}
            />
            <Input
              label="Position/Title"
              placeholder="e.g., CEO, CTO, VP Sales"
              value={discoveryConfig?.criteria?.position}
              onChange={(e) => handleCriteriaChange('position', e?.target?.value)}
            />
            <Input
              label="Location"
              placeholder="e.g., San Francisco, CA"
              value={discoveryConfig?.criteria?.location}
              onChange={(e) => handleCriteriaChange('location', e?.target?.value)}
            />
            <Select
              label="Company Size"
              value={discoveryConfig?.criteria?.companySize}
              onChange={(value) => handleCriteriaChange('companySize', value)}
              options={[
                { value: '', label: 'Any Size' },
                { value: '1-10', label: '1-10 employees' },
                { value: '11-50', label: '11-50 employees' },
                { value: '51-200', label: '51-200 employees' },
                { value: '201-1000', label: '201-1000 employees' },
                { value: '1000+', label: '1000+ employees' }
              ]}
            />
          </div>

          <Input
            label="Keywords"
            placeholder="Additional keywords to target"
            value={discoveryConfig?.criteria?.keywords}
            onChange={(e) => handleCriteriaChange('keywords', e?.target?.value)}
          />

          {/* Advanced Options */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Advanced Options
            </label>
            <div className="space-y-2">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="aiFiltering"
                  checked={discoveryConfig?.advanced?.aiFiltering}
                  onChange={() => handleAdvancedToggle('aiFiltering')}
                />
                <label htmlFor="aiFiltering" className="text-sm text-foreground cursor-pointer">
                  AI-powered lead filtering and scoring
                </label>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="duplicateDetection"
                  checked={discoveryConfig?.advanced?.duplicateDetection}
                  onChange={() => handleAdvancedToggle('duplicateDetection')}
                />
                <label htmlFor="duplicateDetection" className="text-sm text-foreground cursor-pointer">
                  Duplicate detection and removal
                </label>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="realTimeValidation"
                  checked={discoveryConfig?.advanced?.realTimeValidation}
                  onChange={() => handleAdvancedToggle('realTimeValidation')}
                />
                <label htmlFor="realTimeValidation" className="text-sm text-foreground cursor-pointer">
                  Real-time contact validation
                </label>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between pt-4">
            <div className="text-sm text-muted-foreground">
              Max Results: {discoveryConfig?.advanced?.maxResults?.toLocaleString()}
            </div>
            <Button
              onClick={() => onStartDiscovery?.(discoveryConfig)}
              disabled={isProcessing}
              className="min-w-[120px]"
            >
              {isProcessing ? (
                <>
                  <Icon name="Loader2" size={16} className="mr-2 animate-spin" />
                  Discovering...
                </>
              ) : (
                <>
                  <Icon name="Search" size={16} className="mr-2" />
                  Start Discovery
                </>
              )}
            </Button>
          </div>
        </div>
      </div>

      {/* Discovered Leads Results */}
      {discoveredLeads?.length > 0 && (
        <div className="bg-card border border-border rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-foreground">
              Discovered Leads ({discoveredLeads?.length})
            </h3>
            <div className="flex items-center space-x-2">
              <Button
                variant="outline"
                onClick={handleSelectAll}
                size="sm"
              >
                {selectedLeads?.length === discoveredLeads?.length ? 'Deselect All' : 'Select All'}
              </Button>
            </div>
          </div>

          <div className="space-y-3">
            {discoveredLeads?.map((lead) => (
              <div 
                key={lead?.id}
                className={`p-4 border rounded-lg transition-colors ${
                  selectedLeads?.includes(lead?.id) 
                    ? 'border-primary bg-primary/5' :'border-border hover:border-muted-foreground'
                }`}
              >
                <div className="flex items-start space-x-3">
                  <Checkbox
                    checked={selectedLeads?.includes(lead?.id)}
                    onChange={() => handleLeadSelect(lead?.id)}
                  />
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <h4 className="font-medium text-foreground">{lead?.name}</h4>
                        <p className="text-sm text-muted-foreground">
                          {lead?.title} at {lead?.company}
                        </p>
                      </div>
                      <div className="flex items-center space-x-2">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          getValidationStatusColor(lead?.validationStatus)
                        }`}>
                          {lead?.validationStatus?.replace('_', ' ')?.toUpperCase()}
                        </span>
                        <span className={`text-sm font-medium ${
                          getQualityScoreColor(lead?.aiConfidence)
                        }`}>
                          {lead?.aiConfidence}%
                        </span>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs text-muted-foreground">
                      <div className="flex items-center space-x-1">
                        <Icon name="Mail" size={12} />
                        <span className={lead?.emailVerified ? 'text-green-600' : 'text-red-600'}>
                          {lead?.emailVerified ? 'Verified' : 'Unverified'}
                        </span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <Icon name="Phone" size={12} />
                        <span className={lead?.phoneVerified ? 'text-green-600' : 'text-red-600'}>
                          {lead?.phoneVerified ? 'Verified' : 'Unverified'}
                        </span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <Icon name="MapPin" size={12} />
                        <span>{lead?.location}</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <Icon name="Database" size={12} />
                        <span>{lead?.source}</span>
                      </div>
                    </div>

                    {lead?.tags?.length > 0 && (
                      <div className="flex items-center space-x-1 mt-2">
                        {lead?.tags?.map((tag) => (
                          <span 
                            key={tag}
                            className="px-2 py-1 bg-muted text-muted-foreground text-xs rounded"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default LeadDiscoveryPanel;